import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TempImage from "../../../assets/ProductImage.webp";
import { Box, Checkbox, IconButton, Typography } from "@mui/material";
import { DropDownIcon } from "../../../Components/Layouts/CustomIcons";
import {
  VegetarianIcon,
  VeganIcon,
  FishIcon,
  GlutenIcon,
  LactoseIcon,
  ProteinIcon,
} from "../../../Components/Layouts/CustomIcons";
import AccessTimeSharpIcon from "@mui/icons-material/AccessTimeSharp";

const tags = [
  { IconComponent: VegetarianIcon, label: "Vegetarian" },
  { IconComponent: VeganIcon, label: "Vegan" },
  { IconComponent: FishIcon, label: "Fish-Free" },
  { IconComponent: GlutenIcon, label: "Gluten-Free" },
  { IconComponent: LactoseIcon, label: "Lactose-Free" },
  { IconComponent: ProteinIcon, label: "High Protein" },
];

function ProductCard({
  product,
  sendSelectedCard,
  selectionMode = false,
  isSelected = false,
  onSelect,
}) {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
    // console.log("PRODUCT CARD DATA:", product);

  const [isAllergenOpen, setIsAllergenOpen] = useState(false);
  const [isPriceOpen, setIsPriceOpen] = useState(false);

  const [descExpanded, setDescExpanded] = useState(false);
  const [showSeeMore, setShowSeeMore] = useState(false);
  const descRef = useRef(null);

  useEffect(() => {
    if (descRef.current) {
      setShowSeeMore(descRef.current.scrollHeight > 150);
    }
  }, [product.description]);

  const toggleAllergenDropdown = () => {
    setIsAllergenOpen((prev) => {
      if (!prev) setIsPriceOpen(false); // Close price dropdown if opening allergen dropdown
      return !prev;
    });
  };

  const togglePriceDropdown = () => {
    setIsPriceOpen((prev) => {
      if (!prev) setIsAllergenOpen(false); // Close allergen dropdown if opening price dropdown
      return !prev;
    });
  };

  const backgroundCard =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const inputBoxColor =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const dotsColor =
    mode === "dark" ? DARKCOLORS.GreyScale70 : LIGHTCOLORS.GreyScale70;
  const textborderColor =
    mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
  const digitsColor =
    mode === "dark"
      ? DARKCOLORS.BodyLightBackground
      : LIGHTCOLORS.BodyLightBackground;
  const dropCircle =
    mode === "dark" ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;
  const dropIcon =
    mode === "dark" ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;
  const clockColor =
    mode === "dark" ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500;
  const selectedBorderColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;

  // Handle icon click and send selected card to the parent (always opens edit modal)
  const handleIconClick = () => {
    sendSelectedCard(product);
  };

  // Handle card click: toggle selection or open edit modal
  const handleCardClick = () => {
    if (selectionMode) {
      onSelect(product.productId);
    } else {
      sendSelectedCard(product);
    }
  };

  // Function to get the tag icon with optional slash overlay
  const getTagIcon = (tagLabel) => {
    const tag = tags.find(
      (tag) => tag.label.toLowerCase() === tagLabel.toLowerCase(),
    );
    if (!tag || !tag.IconComponent) return null;

    const Icon = tag.IconComponent;
    const noSlash = ["vegan", "vegetarian", "high protein"].includes(
      tagLabel.toLowerCase(),
    );

    return (
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 30,
          height: 30,
          borderRadius: "50%",
          padding: "5px",
          backgroundColor: "white", // or use theme
          overflow: "hidden",
        }}
      >
        <Icon width="20" height="20" />

        {!noSlash && (
          <span
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "100%",
              height: "1px",
              backgroundColor: "black", // Adjust to contrast with background
              transform: "translate(-50%, -50%) rotate(130deg)",
              transformOrigin: "center",
            }}
          />
        )}
      </span>
    );
  };

  return (
    <>
      <Box
        // onClick={handleCardClick}
        sx={{
          background: inputBoxColor,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          justifyContent: "space-between", // Ensures top and bottom alignment
          overflow: "hidden",
          position: "relative",
          cursor: selectionMode ? "pointer" : "default",
          transition: "border-color 0.2s ease",
        }}
        className="product-border-sec"
      >
        {/* Selection Checkbox */}
        {selectionMode && (
          <Checkbox
            checked={isSelected}
            onChange={() => onSelect(product.productId)}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 10,
              p: "0.5px",
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.18)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
              color: inputBoxColor,
              borderRadius: "4px",
              "&.Mui-checked": {
                color: selectedBorderColor,
              },
            }}
          />
        )}

        {/* Top Section */}
        <Box>
          {/* Product Image */}
          <Box sx={{ position: "relative" }}>
            <img
              src={product.imageUrl || TempImage}
              alt={product.productName}
              className="product-border-sec12"
              onError={(e) => {
                e.target.onerror = null; // Prevent infinite loop
                e.target.src = TempImage;
              }}
            />

            {/* Tags: Icons at the bottom-right corner */}
            {product?.tags && product?.tags?.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: "10px",
                  right: "10px",
                  display: "flex",
                  gap: "5px",
                }}
              >
                {product?.tags.map((tag, index) => (
                  <Box
                    key={index}
                    sx={{
                      // backgroundColor: "#fff",
                      // borderRadius: "50%",
                      // padding: "5px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {getTagIcon(tag)}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Product Details */}
          <Box sx={{ padding: "5px 10px" }} sty>
            {/* Product Name */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.5rem",
                gap: "10px",
              }}
            >
              <p className="machine-name-text">{product.productName}</p>
              <MoreVertIcon
                sx={{ color: dotsColor, cursor: "pointer" }}
                fontSize="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleIconClick();
                }}
              />
            </Box>
            <Typography
              className="product-desc-text"
              sx={{
                color: digitsColor,
                display: "-webkit-box",
                WebkitLineClamp: 5,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "normal",
              }}
            >
              {product.description}
            </Typography>
          </Box>
        </Box>

        {/* Bottom Section */}
        <Box sx={{ padding: "5px 10px" }}>
          {/* Allergen */}
          <Box
            className="allergen-box-sec"
            sx={{
              marginBottom: "0.5rem",
              gap: "10px",
              border: `0.5px solid ${textborderColor}`,
              borderRadius: "6px",
            }}
          >
            {/* Dropdown Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={toggleAllergenDropdown}
            >
              <p className="venidng-sec-card-text">{t("inventory.allergen")}</p>
              <IconButton size="small" className="venidng-sec-card-text">
                <DropDownIcon
                  color1={dropCircle}
                  color2={dropIcon}
                  style={{
                    transform: isAllergenOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                  }}
                />
              </IconButton>
            </Box>
            {isAllergenOpen && (
              <p className="product-desc-text">{product.allergyInfo}</p>
            )}
          </Box>

          {/* Price Dropdown */}
          <Box
            className="allergen-box-sec1"
            sx={{
              marginBottom: "0.5rem",
              background: backgroundCard,
              borderRadius: "6px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={togglePriceDropdown}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <p className="venidng-sec-card-text">
                  {t("vendingMachines.price")}
                </p>
                <IconButton size="small" className="venidng-sec-card-text">
                  <DropDownIcon
                    color1={dropCircle}
                    color2={dropIcon}
                    style={{
                      transform: isPriceOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </IconButton>
              </Box>
              <p
                className="popup-text"
                style={{ margin: "0px", color: digitsColor }}
              >
                {(product.actualPrice ?? 0) ===
                (product.discountedPrice ?? 0) ? (
                  `€ ${(product.actualPrice ?? 0).toFixed(2)}`
                ) : (
                  <>
                    <s
                      style={{
                        color: "gray",
                        marginRight: "8px",
                        fontSize: "12px",
                      }}
                    >
                      € {(product.actualPrice ?? 0).toFixed(2)}
                    </s>
                   €{(product.discountedPrice ?? 0).toFixed(2)}
                  </>
                )}
              </p>
            </Box>
            {isPriceOpen && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "15px 0px 5px",
                }}
              >
                <p className="venidng-sec-card-text">
                  {t("inventory.heatTime1")}
                </p>
                <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <AccessTimeSharpIcon
                    style={{ color: clockColor, fontSize: "16px" }}
                  />
                  <p
                    className="popup-text"
                    style={{ color: digitsColor, margin: "0px" }}
                  >{`${product.heatTime || 0} Sec`}</p>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default ProductCard;
